import React, { useEffect, useState, useCallback, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import UnitCount from "../Dashboard/Child/UnitCount";
import { useAuth } from "../../context/AuthContext";
import { generateSettlementWeeks } from "../../utils/helper";

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const AnalyticsDashboardLayer = ({ mode = "reports" }) => {
  const isDashboard = mode === "dashboard";
  const { AsyncGetApiCall } = useApi();
  const { subAdminId } = useAuth();
  const [loading, setLoading] = useState(false);

  // Filters
  const [period, setPeriod] = useState(isDashboard ? "TODAY" : "ALL");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [settlementWeek, setSettlementWeek] = useState("");
  const settlementWeeks = useMemo(() => generateSettlementWeeks(10), []);

  const [revenueData, setRevenueData] = useState([]);
  const [segmentData, setSegmentData] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [instrumentLeaderboard, setInstrumentLeaderboard] = useState([]);
  const [brokerPerformance, setBrokerPerformance] = useState([]);
  const [cardStats, setCardStats] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    const query = settlementWeek
      ? { settlementWeek }
      : showCustomDates
        ? customRange
        : { period };

    // Clean empty values
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(([_, v]) => v),
    );

    const requests = [
      // Chart: Always show a trend (Last 6 Months) on Dashboard, or use filters on Reports
      AsyncGetApiCall("/analytics/revenue-chart", isDashboard ? { period: "LAST_6_MONTHS" } : cleanQuery),
      // Distribution & Cards: Use 'TODAY' on Dashboard, or filters on Reports
      AsyncGetApiCall("/analytics/segment-distribution", cleanQuery),
      AsyncGetApiCall("/analytics/top-users", cleanQuery),
      AsyncGetApiCall("/analytics/dashboard-main-cards", cleanQuery),
    ];

    if (!isDashboard) {
      requests.push(AsyncGetApiCall("/analytics/instrument-leaderboard", cleanQuery));
      requests.push(AsyncGetApiCall("/analytics/broker-performance", cleanQuery));
    }

    const [revRes, segRes, topRes, cardRes, instRes, brokerRes] = await Promise.all(requests);

    if (revRes?.success) setRevenueData(revRes.data.monthlyData);
    if (segRes?.success) setSegmentData(segRes.data);
    if (topRes?.success) setTopUsers(topRes.data);
    if (cardRes?.success) setCardStats(cardRes.data);
    if (instRes?.success) setInstrumentLeaderboard(instRes.data);
    if (brokerRes?.success) setBrokerPerformance(brokerRes.data);

    setLoading(false);
  }, [AsyncGetApiCall, period, customRange, showCustomDates, isDashboard]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    if (val === "CUSTOM") {
      setShowCustomDates(true);
      setPeriod("CUSTOM");
    } else {
      setShowCustomDates(false);
      setPeriod(val);
    }
  };

  const revenueChartOptions = {
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    xaxis: {
      categories: revenueData.map(
        (d) => `${monthNames[d.month - 1]} ${d.year}`,
      ),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { formatter: (val) => `₹${val}` } },
    colors: ["#3d5ee1", "#28c76f"],
    tooltip: { y: { formatter: (val) => `₹${val}` } },
  };

  const segmentChartOptions = {
    chart: { type: "donut" },
    labels: segmentData.map((s) => s.segment),
    colors: ["#3d5ee1", "#28c76f", "#ff9f43", "#ea5455", "#00cfe8"],
    legend: { position: "bottom" },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Revenue",
              formatter: () =>
                `₹${segmentData.reduce((acc, curr) => acc + curr.revenue, 0).toFixed(0)}`,
            },
          },
        },
      },
    },
  };

  return (
    <>
      {loading && <Loader />}

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h5 className="mb-8">
            {isDashboard ? "Platform Pulse" : (subAdminId ? "Master Performance" : "Platform Performance")}
          </h5>
          <p className="text-secondary-light mb-0">
            Analysis based on {period.toLowerCase().replace("_", " ")} periods.
          </p>
        </div>

        <div className="d-flex align-items-center gap-3">
          {!isDashboard && (
            <>
              {showCustomDates && (
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customRange.start}
                    onChange={(e) =>
                      setCustomRange({ ...customRange, start: e.target.value })
                    }
                  />
                  <span className="text-secondary-light">to</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customRange.end}
                    onChange={(e) =>
                      setCustomRange({ ...customRange, end: e.target.value })
                    }
                  />
                </div>
              )}
              <select
                className="form-select form-select-sm w-auto radius-8 border-light"
                value={period}
                onChange={handlePeriodChange}
              >
                <option value="ALL">All Time</option>
                <option value="TODAY">Today</option>
                <option value="LAST_MONTH">Last Month</option>
                <option value="LAST_6_MONTHS">Last 6 Months</option>
                <option value="LAST_YEAR">Last Year</option>
                <option value="CUSTOM">Custom Range</option>
              </select>

              <select
                className="form-select form-select-sm w-auto radius-8 border-light"
                value={settlementWeek}
                onChange={(e) => {
                  setSettlementWeek(e.target.value);
                  if (e.target.value) {
                    setPeriod("");
                    setShowCustomDates(false);
                  }
                }}
              >
                <option value="">Settlement Week</option>
                {settlementWeeks.map((w) => (
                  <option key={w.value} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            className="btn btn-primary-600 btn-sm radius-8 px-16"
            onClick={fetchAnalytics}
          >
            {isDashboard ? "Refresh Pulse" : "Apply"}
          </button>
        </div>
      </div>

      <UnitCount data={cardStats} />

      <div className="row gy-4 mt-1">
        <div className="col-12 col-xxl-8">
          <div className="card h-100 border shadow-sm radius-16">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
              <h6 className="mb-0">
                {subAdminId
                  ? "Revenue & Brokerage History"
                  : "System Revenue Trend"}
              </h6>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary-focus text-primary-600 px-12">
                  Trend View
                </span>
              </div>
            </div>
            <div className="card-body p-24">
              <ReactApexChart
                options={revenueChartOptions}
                series={[
                  {
                    name: "Net Profit",
                    data: revenueData.map((d) => d.revenue),
                  },
                  {
                    name: "Gross Brokerage",
                    data: revenueData.map((d) => d.brokerage),
                  },
                ]}
                type="area"
                height={350}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-xxl-4">
          <div className="card h-100 border shadow-sm radius-16">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="mb-0">Revenue by Segment</h6>
            </div>
            <div className="card-body p-24 d-flex align-items-center justify-content-center">
              {segmentData.length > 0 ? (
                <ReactApexChart
                  options={segmentChartOptions}
                  series={segmentData.map((s) => s.revenue)}
                  type="donut"
                  height={380}
                />
              ) : (
                <div className="text-center text-secondary-light">
                  No data for selected period
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Users Table */}
        <div className="col-12">
          <div className="card border shadow-sm radius-16">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="mb-0">Top Performing Clients</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table b-table table-hover mb-0">
                  <thead className="bg-base">
                    <tr>
                      <th className="px-24 py-16 text-secondary-light">
                        Login ID
                      </th>
                      <th className="px-24 py-16 text-secondary-light">
                        User Name
                      </th>
                      <th className="px-24 py-16 text-secondary-light text-center">
                        Total Trades
                      </th>
                      <th className="px-24 py-16 text-secondary-light text-end">
                        Total Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topUsers.length > 0 ? (
                      topUsers.map((user, idx) => (
                        <tr key={idx}>
                          <td className="px-24 py-12 fw-semibold text-primary-600">
                            {user.loginId}
                          </td>
                          <td className="px-24 py-12">{user.userName}</td>
                          <td className="px-24 py-12 text-center">
                            {user.trades}
                          </td>
                          <td className="px-24 py-12 text-end text-success-600 fw-bold">
                            ₹{user.revenue.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="text-center py-24 text-secondary-light"
                        >
                          No activity found in this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Instrument Leaderboard - Only in Reports */}
        {!isDashboard && (
          <>
            <div className="col-12 col-xxl-6">
              <div className="card border shadow-sm radius-16">
                <div className="card-header border-bottom bg-base py-16 px-24">
                  <h6 className="mb-0">Most Traded Instruments</h6>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table b-table mb-0">
                      <thead className="bg-base">
                        <tr>
                          <th className="px-24 py-16 text-secondary-light">Symbol</th>
                          <th className="px-24 py-16 text-secondary-light text-center">Trade Count</th>
                          <th className="px-24 py-16 text-secondary-light text-end">Volume</th>
                        </tr>
                      </thead>
                      <tbody>
                        {instrumentLeaderboard.length > 0 ? (
                          instrumentLeaderboard.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-24 py-12 fw-semibold text-primary-600">{item.symbol}</td>
                              <td className="px-24 py-12 text-center">{item.trades}</td>
                              <td className="px-24 py-12 text-end">{(item.volume / 100).toFixed(2)} Lots</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="3" className="text-center py-24 text-secondary-light">No instrument data found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Share Breakdown - Only in Reports */}
            <div className="col-12 col-xxl-6">
              <div className="card border shadow-sm radius-16">
                <div className="card-header border-bottom bg-base py-16 px-24">
                  <h6 className="mb-0">Revenue Share Breakdown</h6>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table b-table mb-0">
                      <thead className="bg-base">
                        <tr>
                          <th className="px-24 py-16 text-secondary-light">Sub-Admin / Broker</th>
                          <th className="px-24 py-16 text-secondary-light text-end">Master Share</th>
                          <th className="px-24 py-16 text-secondary-light text-end">Admin Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brokerPerformance.length > 0 ? (
                          brokerPerformance.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-24 py-12 fw-semibold">{item.name}</td>
                              <td className="px-24 py-12 text-end text-primary-600">₹{item.masterRevenue.toFixed(2)}</td>
                              <td className="px-24 py-12 text-end text-success-600">₹{item.adminRevenue.toFixed(2)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="3" className="text-center py-24 text-secondary-light">No share data available</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AnalyticsDashboardLayer;
